/*
 * Copyright (c) 2026. BrightNight. All rights reserved.
 * Unauthorized copying of this file, via any medium is strictly prohibited.
 * Proprietary and confidential.
 */
import z from 'zod';
import { pricingInputs } from './inputs.generated';
import { PLANT_CONFIGURATION_SF_VALUES } from './matrix';
import { isMissingPricingValue, requiredPricingFields, } from './preview';
const NON_NEGATIVE = /MW_ac|MWh_ac|Term|Price|Payment|Cycles/;
/** Valid value for one catalog input, by kind. */
/**
 * Restricted Salesforce picklists on Pricing_Scenario__c. Salesforce rejects the whole record for
 * any other value, so these are validated before upload. Keep in sync with bnCRM.
 */
export const PRICING_PICKLISTS = {
    PPA_Type__c: [
        'As Avail',
        'Capacity Payment',
        'As Avail + Capacity Payment',
        'Structured Product',
    ],
    Delivery_Point__c: ['Bus Bar', 'Hub settled'],
};
const PLANT_PARTS = Object.values(PLANT_CONFIGURATION_SF_VALUES);
/** Typed plant part ("wind", "H2", "Wind") -> bnCRM picklist value. */
const plantPart = (text) => {
    const upper = text.trim().toUpperCase();
    return (PLANT_CONFIGURATION_SF_VALUES[upper] ??
        PLANT_PARTS.find((part) => part.toUpperCase() === upper) ??
        text.trim());
};
/** bnCRM caps integer digits at precision - scale, e.g. percent(4,2) stays below 100. */
const numberLimit = (definition) => 'precision' in definition && 'scale' in definition
    ? 10 ** (definition.precision - definition.scale)
    : undefined;
export function pricingValueSchema(definition) {
    const picklist = PRICING_PICKLISTS[definition.field];
    if (picklist)
        return z.enum(picklist);
    switch (definition.kind) {
        case 'number': {
            const number = NON_NEGATIVE.test(definition.field) ? z.number().min(0) : z.number();
            const limit = numberLimit(definition);
            return limit === undefined ? number : number.refine((value) => Math.abs(value) < limit);
        }
        case 'array':
            return z
                .array(z.enum(PLANT_PARTS))
                .min(1)
                .refine((parts) => new Set(parts).size === parts.length);
        case 'date':
            return z.iso.date();
        default:
            return z.string();
    }
}
/** The single per-input rule set; also used by the UI to derive row status. */
export function pricingInputIssue(input, required) {
    const definition = pricingInputs.find(({ field }) => field === input.field);
    if (!definition)
        return 'Choose a supported input.';
    if (input.unit !== definition.unit)
        return `${definition.label}: use ${definition.unit || 'no unit'}.`;
    if (isMissingPricingValue(input.value))
        return required ? `${definition.label} is required.` : undefined;
    const limit = numberLimit(definition);
    if (typeof input.value === 'number' && limit !== undefined && Math.abs(input.value) >= limit)
        return `${definition.label}: bnCRM accepts values below ${limit}.`;
    if (!pricingValueSchema(definition).safeParse(input.value).success)
        return PRICING_PICKLISTS[definition.field]
            ? `${definition.label}: choose a bnCRM option.`
            : `${definition.label}: enter a valid ${definition.kind}.`;
    return undefined;
}
const draftInputSchema = z.object({
    field: z.string(),
    value: z.union([z.string(), z.number(), z.array(z.string()), z.null()]),
    unit: z.string(),
    confidence: z.enum(['low', 'medium', 'high']),
});
const draftScenarioShape = {
    row_id: z.uuid().optional(),
    // Same bounds as pricingPreviewSchema, so the form blocks what the server would reject.
    source_scenario: z
        .string()
        .trim()
        .min(1, 'Enter a source scenario name.')
        .max(255, 'Use at most 255 characters.'),
    scenario_id: z.int().nullable(),
    confidence: z.enum(['low', 'medium', 'high']),
};
function checkScenario(scenario, index, matrix, seen, ctx) {
    const issue = (path, message) => ctx.addIssue({ code: 'custom', path: ['scenarios', index, ...path], message });
    const matched = matrix.find(({ id }) => id === scenario.scenario_id);
    if (!matched)
        return issue(['scenario_id'], 'Choose a bid matrix match.');
    if (seen.has(matched.id))
        issue(['scenario_id'], 'Duplicate bid matrix match.');
    seen.add(matched.id);
    const required = requiredPricingFields(matched);
    const fields = new Set();
    for (const [position, input] of scenario.inputs.entries()) {
        const label = pricingInputs.find(({ field }) => field === input.field)?.label ?? input.field;
        if (fields.has(input.field))
            issue(['inputs', position, 'field'], `${label} is duplicated.`);
        fields.add(input.field);
        const message = pricingInputIssue(input, required.includes(input.field));
        if (message)
            issue(['inputs', position, 'value'], message);
    }
    for (const field of required.filter((key) => !fields.has(key)))
        issue(['inputs'], `${pricingInputs.find((input) => input.field === field)?.label} is required.`);
}
/** Business rules for an editable preview; issue paths point at the offending field. */
export function pricingPreviewChecksSchema(matrix) {
    return z
        .object({
        scenarios: z.array(z.object({ ...draftScenarioShape, inputs: z.array(draftInputSchema) })),
    })
        .superRefine((preview, ctx) => {
        if (!preview.scenarios.length)
            ctx.addIssue({
                code: 'custom',
                path: ['scenarios'],
                message: 'Add at least one scenario.',
            });
        const seen = new Set();
        for (const [index, scenario] of preview.scenarios.entries())
            checkScenario(scenario, index, matrix, seen, ctx);
    });
}
// Editable text <-> API values. Arrays are "PV+BESS"; numbers are numeric strings.
function fromPricingText(text, field) {
    if (!text.trim())
        return null;
    const kind = pricingInputs.find((input) => input.field === field)?.kind;
    if (kind === 'array')
        return text.split('+').map(plantPart);
    if (kind === 'number' && Number.isFinite(Number(text)))
        return Number(text);
    return text;
}
function toPricingText(value) {
    if (Array.isArray(value))
        return value.join('+');
    return value == null ? '' : String(value);
}
export const toPricingFormScenario = (scenario) => ({
    ...scenario,
    inputs: scenario.inputs.map((input) => ({ ...input, value: toPricingText(input.value) })),
});
export const fromPricingFormScenario = (scenario) => ({
    ...scenario,
    inputs: scenario.inputs.map((input) => ({
        ...input,
        value: fromPricingText(input.value, input.field),
    })),
});
export const toPricingForm = (preview) => ({
    scenarios: preview.scenarios.map(toPricingFormScenario),
});
export const fromPricingForm = (form) => ({
    scenarios: form.scenarios.map(fromPricingFormScenario),
});
/** Form schema: decodes text inputs, then applies the preview business rules. */
export function pricingPreviewFormSchema(matrix) {
    const formInputSchema = draftInputSchema.extend({ value: z.string() });
    return z.codec(z.object({
        scenarios: z.array(z.object({ ...draftScenarioShape, inputs: z.array(formInputSchema) })),
    }), pricingPreviewChecksSchema(matrix), {
        decode: (form) => fromPricingForm(form),
        encode: (preview) => toPricingForm(preview),
    });
}
/** Server-side validation of a confirmed preview, plus the records to create. */
export function validatePricingPreview(preview, matrix) {
    const result = pricingPreviewChecksSchema(matrix).safeParse(preview);
    const scenarioIssues = preview.scenarios.map(() => []);
    const issues = (result.error?.issues ?? []).map(({ path, message }) => {
        const index = path[1];
        if (typeof index !== 'number')
            return message;
        scenarioIssues[index]?.push(message);
        return `Scenario ${index + 1}: ${message}`;
    });
    const records = preview.scenarios.flatMap((proposal) => {
        const index = matrix.findIndex(({ id }) => id === proposal.scenario_id);
        if (index < 0)
            return [];
        const values = Object.fromEntries(proposal.inputs.map(({ field, value }) => [field, value]));
        return [
            {
                ...values,
                PA_Bid_Request_Scenario_ID2__c: matrix[index].id,
                Bid_Request_Scenario__c: index + 1,
            },
        ];
    });
    return { issues, scenarioIssues, records };
}
//# sourceMappingURL=preview-schema.js.map